'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ensureRecaptchaConfig,
  formatAuthError,
  startEmailOTP,
  verifyEmailOTPAndSignIn,
  signInWithFacebookAccessToken,
  signInWithOAuth,
  type OAuthProviderId,
} from './authActions';
import { shouldSkipFirebaseEmailCredentialApply } from './emailCredentialApply';
import { useFacebookLoginEnabled } from './facebookLoginEnabled';
import { useLogin } from './LoginContext';
import { GoogleSignInButton } from './GoogleSignInButton';
import { FacebookSignInButton } from './FacebookSignInButton';
import { RecaptchaNotice } from './RecaptchaNotice';
import { emailOtpSentMessage } from './emailOtpCopy';
import { TermsRequiredError } from './establishSession';
import { JoinGateAlert } from './JoinGateAlert';
import { JoinGateError, joinGateFromUnknown } from './joinGate';
import { attachGuestAfterLogin } from '@/platform/commerce/api';
import { returnToSlugAuthOrigin } from '@/platform/lib/idTokenStore';
import {
  finishApexHostLoginWithDefaults,
  resolveApexHostLoginDestWithDefaults,
} from '@/platform/saas/apexHostLoginFinish';
import { hostAwareReturnTo } from '@/platform/saas/hostPostLogin';

type Props = {
  headingId?: string;
  returnTo?: string;
  onSuccess?: () => void;
  externalError?: string | null;
  onClearExternalError?: () => void;
};

export function LoginForm({
  headingId,
  returnTo: returnToProp,
  onSuccess,
  externalError,
  onClearExternalError,
}: Props) {
  const router = useRouter();
  const { returnTo: contextReturnTo, persistReturnTo, inviteEmailHint } = useLogin();
  const returnTo = returnToProp ?? contextReturnTo;
  const { enabled: facebookLogin, loading: facebookLoginLoading } = useFacebookLoginEnabled();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'verifying' | 'error'>('idle');
  const [oauthProvider, setOauthProvider] = useState<OAuthProviderId | null>(null);
  const [error, setError] = useState('');
  const [joinGate, setJoinGate] = useState<JoinGateError | null>(null);

  const displayError = externalError || error;

  // Member invite links: context hint and/or ?invite=1&email=…
  useEffect(() => {
    const hint =
      (inviteEmailHint || '').trim() ||
      (typeof window !== 'undefined'
        ? (() => {
            const q = new URLSearchParams(window.location.search);
            return q.get('invite') === '1' ? (q.get('email') || '').trim() : '';
          })()
        : '');
    if (hint.includes('@')) {
      setEmail((prev) => prev || hint);
    }
  }, [inviteEmailHint]);

  // Auth stays on this host (modal + API).
  useEffect(() => {
    ensureRecaptchaConfig().catch(() => {
      /* reCAPTCHA loads when user submits; pre-warm is best-effort */
    });
  }, []);

  const finishLogin = useCallback(async () => {
    try {
      if (shouldSkipFirebaseEmailCredentialApply()) {
        const dest = await hostAwareReturnTo(returnTo);
        onSuccess?.();
        if (dest.startsWith('/') && dest !== '/') {
          router.push(dest);
        }
        return;
      }
      const { dest, bounced } = await finishApexHostLoginWithDefaults(returnTo);
      try {
        await attachGuestAfterLogin();
      } catch {
        /* best-effort idempotent guest cart/order attach */
      }
      onSuccess?.();
      if (!bounced && dest.startsWith('/') && dest !== '/') {
        router.push(dest);
      }
    } catch (err) {
      if (err instanceof TermsRequiredError) {
        // Session cookies may already be set; TermsGate shows accept on the destination.
        onSuccess?.();
        const { dest, bounced } = await resolveApexHostLoginDestWithDefaults(returnTo);
        if (!bounced && dest.startsWith('/') && dest !== '/') {
          router.push(dest);
        }
        return;
      }
      const gate = joinGateFromUnknown(err);
      if (gate) {
        if (returnToSlugAuthOrigin({ error: gate.message })) return;
        setJoinGate(gate);
        return;
      }
      throw err;
    }
  }, [returnTo, router, onSuccess]);

  async function onEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    onClearExternalError?.();
    setError('');
    setJoinGate(null);
    setStatus('sending');
    try {
      persistReturnTo();
      await startEmailOTP(email.trim(), { returnTo });
      setStatus('sent');
      setCode('');
    } catch (err) {
      setStatus('error');
      setError(formatAuthError(err));
    }
  }

  async function onVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    onClearExternalError?.();
    setError('');
    setJoinGate(null);
    setStatus('verifying');
    try {
      await verifyEmailOTPAndSignIn(email.trim(), code.trim());
      await finishLogin();
    } catch (err) {
      setStatus('sent');
      setError(formatAuthError(err));
    }
  }

  async function onFacebookAccessToken(accessToken: string) {
    onClearExternalError?.();
    setError('');
    setJoinGate(null);
    setOauthProvider('facebook');
    try {
      await signInWithFacebookAccessToken(accessToken);
      await finishLogin();
      setOauthProvider(null);
    } catch (err) {
      setError(formatAuthError(err));
      setOauthProvider(null);
    }
  }

  async function onOAuth(provider: OAuthProviderId) {
    onClearExternalError?.();
    setError('');
    setJoinGate(null);
    setOauthProvider(provider);
    try {
      persistReturnTo();
      const cred = await signInWithOAuth(provider);
      if (cred) {
        await finishLogin();
        setOauthProvider(null);
      } else {
        setOauthProvider(null);
      }
    } catch (err) {
      const message = formatAuthError(err);
      if (!message.startsWith('Redirecting to')) {
        setError(message);
        setOauthProvider(null);
      }
    }
  }

  const oauthBusy = oauthProvider !== null;
  const emailLinkOnly =
    typeof process !== 'undefined' && process.env.NEXT_PUBLIC_AUTH_EMAIL_LINK_ONLY === 'true';
  const showOAuth = !emailLinkOnly;
  const emailBusy = status === 'sending' || status === 'verifying';

  return (
    <div className="login-form">
      <h2 id={headingId} className="login-form__title">
        Log in or create an account
      </h2>

      {joinGate ? <JoinGateAlert error={joinGate} /> : displayError ? <p className="form-error">{displayError}</p> : null}

      {showOAuth && (
        <div className="login-form__oauth">
          <GoogleSignInButton
            data-testid="oauth-google"
            disabled={oauthBusy}
            aria-busy={oauthProvider === 'google'}
            onClick={() => void onOAuth('google')}
          />
          {facebookLogin && (
            <FacebookSignInButton
              disabled={oauthBusy}
              onAccessToken={onFacebookAccessToken}
              onFallbackClick={() => onOAuth('facebook')}
            />
          )}
          {facebookLoginLoading && (
            <p className="text-muted login-form__oauth-status" aria-live="polite">
              Checking sign-in options…
            </p>
          )}
        </div>
      )}

      {showOAuth && <p className="login-form__divider">or continue with email</p>}

      {status === 'sent' || status === 'verifying' ? (
        <form onSubmit={onVerifyCode}>
          <p className="form-success">{emailOtpSentMessage(email)}</p>
          <label className="field">
            Code
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={emailBusy || oauthBusy}
              data-testid="email-otp-code"
            />
          </label>
          <div className="login-form__email-submit">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={emailBusy || oauthBusy || code.trim().length !== 6}
              data-testid="email-otp-verify"
            >
              {status === 'verifying' ? 'Verifying…' : 'Verify code'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={emailBusy || oauthBusy}
              onClick={() => {
                setStatus('idle');
                setCode('');
              }}
            >
              Change email
            </button>
            <RecaptchaNotice />
          </div>
        </form>
      ) : (
        <form onSubmit={onEmailSubmit}>
          <label className="field">
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={emailBusy || oauthBusy}
              data-testid="email-otp-email"
            />
          </label>
          <div className="login-form__email-submit">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={emailBusy || oauthBusy || !email.trim()}
              data-testid="email-otp-send"
            >
              {status === 'sending' ? 'Sending…' : 'Email me a 6-digit code'}
            </button>
            <RecaptchaNotice />
          </div>
        </form>
      )}
    </div>
  );
}
